import { useEffect, useMemo, useState, useCallback } from "react";
import { playerService } from "../../../services/api";
import { formatMatchDate, normalizeMatchId } from "../utils/playerStatsUtils";

export const usePlayerStats = ({
  playerData,
  playerId,
  setAttributes,
  wyscoutId,
}) => {
  // Selected season's stats object (current selection)
  const [seasonStats, setSeasonStats] = useState(null);
  // All seasons returned by the API (array of season objects)
  const [seasonOptions, setSeasonOptions] = useState([]);
  // Currently selected season_id (string/number)
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [matchHeatmapData, setMatchHeatmapData] = useState([]);
  
  // Ratings state - now managed internally
  const [playerRatings, setPlayerRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsError, setRatingsError] = useState(null);

  // Fetch ratings with season_id
  const fetchRatings = useCallback(async (wyscoutIdParam, seasonIdParam) => {
    if (!wyscoutIdParam) {
      setPlayerRatings([]);
      return;
    }

    setRatingsLoading(true);
    setRatingsError(null);

    try {
      const response = await playerService.getPlayerRatings(wyscoutIdParam, seasonIdParam);
      
      if (response.success) {
        const data = response.data;
        const matches = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
          ? data.results
          : [];
        setPlayerRatings(matches);
      } else {
        setPlayerRatings([]);
        setRatingsError(response.error || "Failed to load player ratings.");
      }
    } catch (error) {
      console.error("Error loading player ratings:", error);
      setPlayerRatings([]);
      setRatingsError("Failed to load player ratings.");
    } finally {
      setRatingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!Array.isArray(playerRatings) || playerRatings.length === 0) {
      setSelectedMatchId(null);
      setMatchHeatmapData([]);
      return;
    }

    setSelectedMatchId((previous) => {
      const normalizedPrevious = normalizeMatchId(previous);
      if (
        normalizedPrevious &&
        playerRatings.some(
          (item) => normalizeMatchId(item?.match?.id) === normalizedPrevious
        )
      ) {
        return normalizedPrevious;
      }
      return normalizeMatchId(playerRatings[0]?.match?.id);
    });
  }, [playerRatings]);

  useEffect(() => {
    if (!playerId) return;

    let isMounted = true;

    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const response = await playerService.getPlayerStats(playerId);
        if (!isMounted) return;

        if (!response.success) {
          console.error("Error fetching player stats:", response.error);
          setSeasonOptions([]);
          setSeasonStats(null);
          setSelectedSeasonId(null);
          return;
        }

        // New API returns an array of seasons; gracefully handle object fallback
        const payload = response.data;
        const seasons = Array.isArray(payload)
          ? payload
          : payload && typeof payload === "object"
          ? [payload]
          : [];
        // Filter out seasons that have no blocks (empty or missing)
        const filteredSeasons = seasons.filter(
          (s) => Array.isArray(s?.blocks) && s.blocks.length > 0
        );

        setSeasonOptions(filteredSeasons);

        // Auto-select previously selected season if still available; otherwise first one
        const nextSelectedSeasonId = filteredSeasons.length
          ? filteredSeasons[0]?.season_id ?? null
          : null;

        setSelectedSeasonId(nextSelectedSeasonId);

        // Derive selected season object
        const selected =
          filteredSeasons.find(
            (s) => String(s?.season_id) === String(nextSelectedSeasonId)
          ) ||
          filteredSeasons[0] ||
          null;
        setSeasonStats(selected);
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching player stats:", error);
          setSeasonOptions([]);
          setSeasonStats(null);
          setSelectedSeasonId(null);
        }
      } finally {
        if (isMounted) {
          setStatsLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, [playerId]);

  // Keep derived selected season object in sync when selection/options change
  useEffect(() => {
    if (!Array.isArray(seasonOptions) || seasonOptions.length === 0) {
      setSeasonStats(null);
      return;
    }
    if (!selectedSeasonId) {
      setSeasonStats(seasonOptions[0] || null);
      return;
    }
    const selected =
      seasonOptions.find(
        (s) => String(s?.season_id) === String(selectedSeasonId)
      ) || seasonOptions[0] || null;
    setSeasonStats(selected);
  }, [seasonOptions, selectedSeasonId]);

  // Fetch ratings when wyscoutId or selectedSeasonId changes
  useEffect(() => {
    if (!wyscoutId || !selectedSeasonId) {
      return;
    }
    fetchRatings(wyscoutId, selectedSeasonId);
  }, [wyscoutId, selectedSeasonId, fetchRatings]);

  const selectedMatch = useMemo(() => {
    if (!Array.isArray(playerRatings) || !selectedMatchId) {
      return null;
    }

    return (
      playerRatings.find(
        (item) => normalizeMatchId(item?.match?.id) === selectedMatchId
      ) || null
    );
  }, [playerRatings, selectedMatchId]);

  const ratingChartData = useMemo(() => {
    if (!Array.isArray(playerRatings) || playerRatings.length === 0) {
      return {
        title: "Average Rating",
        currentRating: "",
        dataPoints: [],
        noData: true,
      };
    }

    const parseRating = (value) => {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
      }
      if (typeof value === "string") {
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    // Helper to build image URL - handles full URLs and hash-based paths
    const buildImageUrl = (hashOrUrl) => {
      if (!hashOrUrl) return "";
      const value = String(hashOrUrl);
      // If already a full URL, use directly
      if (value.startsWith("http://") || value.startsWith("https://")) {
        return value;
      }
      // Otherwise construct sportdevs URL
      return `https://images.sportdevs.com/${value}.png`;
    };

    const dataPoints = playerRatings
      .filter((item) => item?.rating !== null && item?.rating !== undefined)
      .map((item) => {
        const ratingValue = parseRating(item?.rating);
        const opponentName = item?.opponent?.name || "-";
        const opponentHashImage = item?.opponent?.hash_image || null;

        // Logic to determine match result (W/D/L)
        // Priority 1: Use result directly from API if available
        let matchResult = item?.result || null;
        
        // Priority 2: Compute from score if not provided directly
        if (!matchResult && item?.match) {
             const score = item.match.score || {};
             const homeScore = score.home ?? item.match.home_score;
             const awayScore = score.away ?? item.match.away_score;
             const { home_team_id, away_team_id, winner_team_id } = item.match;
             const playerTeamId = item.player_team_id;

             // Method 1: Explicit winner_team_id
             if (winner_team_id) {
                 if (winner_team_id === playerTeamId) matchResult = 'W';
                 else matchResult = 'L';
             } 
             // Method 2: Score comparison
             else if (homeScore !== undefined && awayScore !== undefined && playerTeamId) {
                 const hScore = parseInt(homeScore);
                 const aScore = parseInt(awayScore);
                 if (!isNaN(hScore) && !isNaN(aScore)) {
                     if (hScore === aScore) matchResult = 'D';
                     else if (playerTeamId === home_team_id) matchResult = hScore > aScore ? 'W' : 'L';
                     else if (playerTeamId === away_team_id) matchResult = aScore > hScore ? 'W' : 'L';
                 }
             }
        }

        return {
          id: normalizeMatchId(item?.match?.id),
          date: formatMatchDate(item?.match?.kickoff_at),
          rawDate: item?.match?.kickoff_at,
          rating: ratingValue,
          result: matchResult, // New field for Form Guide
          opponent: opponentName,
          competition: item?.match?.league_name || "-",
          minutes: null,
          goals: null,
          assists: null,
          xg: null,
          cards: null,
          shots: null,
          teamId: item?.player_team_id || null,
          opponentId: item?.opponent?.id || null,
          country: null,
          countryName: "",
          countryImage: buildImageUrl(opponentHashImage),
        };
      })
      .filter((point) => Number.isFinite(point.rating));

    const currentRatingValue = (() => {
      if (selectedMatch) {
        return parseRating(selectedMatch?.rating);
      }
      return dataPoints[0]?.rating ?? null;
    })();

    const currentRating = Number.isFinite(currentRatingValue)
      ? currentRatingValue.toFixed(1)
      : "";

    return {
      title: "Average Rating",
      currentRating,
      dataPoints,
      noData: dataPoints.length === 0,
    };
  }, [playerRatings, selectedMatch]);

  return {
    // Selected season object (for current view)
    seasonStats,
    // All available seasons to choose from
    seasonOptions,
    selectedSeasonId,
    setSelectedSeasonId,
    statsLoading,
    selectedMatchId,
    setSelectedMatchId,
    selectedMatch,
    matchHeatmapData,
    ratingChartData,
    // Ratings state
    ratingsLoading,
    ratingsError,
  };
};
